import {
  forwardRef,
  createContext,
  Dispatch,
  SetStateAction,
  useState,
  useRef,
  useEffect,
  ComponentPropsWithoutRef,
  useMemo,
} from "react";
import { StepperStep } from "./StepperStep";
import { StepperList } from "./StepperList";
import { StepperPanel } from "./StepperPanel";
import styles from "./Stepper.module.scss";
import cx from "classnames";
import { StepperNextButton } from "./StepperNextButton";
import { StepperPrevButton } from "./StepperPrevButton";
import { LayoutUtilProps } from "../../types";
import { useLayoutPropsUtil, useMergeRefs } from "../../hooks";
import { StepperFinalPanel } from "./StepperFinalPanel";
import { motion } from "motion/react";

/**
 * Props for the Stepper component
 * @extends ComponentPropsWithoutRef<"div">
 * @extends LayoutUtilProps
 */
export type StepperProps = Omit<
  ComponentPropsWithoutRef<"div">,
  "onAnimationStart" | "onDragStart" | "onDragEnd" | "onDrag" | "onChange"
> &
  LayoutUtilProps & {
    /**
     * Default step to start on.
     * @default undefined
     */
    defaultIndex?: number;

    /**
     * Allows users to navigate to previous steps.
     * @default undefined
     */
    allowNavigateToPrevStep?: boolean;

    /**
     * Returns current step data when step changes.
     * @param data Object containing currentStepId and currentStepIndex
     * @param data.currentStepId ID given to Stepper.Panel, or "final-panel"
     * @param data.currentStepIndex Index of current step, or undefined on final panel
     */
    onChange?: (data: {
      currentStepId: string | "final-panel";
      currentStepIndex?: number;
    }) => void;

    /**
     * Called when all steps are completed.
     */
    onComplete?: () => void;
  };

type StepperContextProps = {
  current: number;
  setCurrent: Dispatch<SetStateAction<number>>;
  currentId: string;
  setCurrentId: Dispatch<SetStateAction<string>>;
  focus: number;
  setFocus: Dispatch<SetStateAction<number>>;
  onComplete?: () => void;
  items?: NodeListOf<HTMLElement>;
  allCompleted?: boolean;
  setAllCompleted?: Dispatch<SetStateAction<boolean>>;
  allowNavigateToPrevStep?: boolean;
};

export const StepperContext = createContext<StepperContextProps>({
  current: 0,
  currentId: "",
  focus: 0,
  setFocus: () => undefined,
  setCurrent: () => undefined,
  setCurrentId: () => undefined,
});

const StepperElement = forwardRef<HTMLDivElement, StepperProps>(
  (props, ref) => {
    const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
    const {
      defaultIndex,
      className,
      allowNavigateToPrevStep,
      children,
      onChange,
      onComplete,
      style,
      ...rest
    } = componentProps;
    const tabRef = useRef<HTMLDivElement>(null);
    const [items, setItems] = useState<NodeListOf<HTMLElement>>();
    const [allCompleted, setAllCompleted] = useState(false);
    const [current, setCurrent] = useState<number>(defaultIndex ?? 0);
    const [currentId, setCurrentId] = useState<string>("");
    const [focus, setFocus] = useState<number>(defaultIndex ?? 0);
    const stepperClassNames = cx(styles["stepper"], className);

    useEffect(() => {
      setItems(tabRef.current?.querySelectorAll("[role=tab]:not([disabled])"));
    }, []);

    useEffect(() => {
      onChange?.({
        currentStepId: currentId,
        currentStepIndex: allCompleted ? undefined : current,
      });
      // id and index change at the same time
    }, [allCompleted, currentId, onChange]);

    const styleCombined = { ...style, ...layoutStyles };

    const value = useMemo(
      () => ({
        current,
        setCurrent,
        currentId,
        setCurrentId,
        focus,
        setFocus,
        onComplete,
        items,
        allCompleted,
        setAllCompleted,
        allowNavigateToPrevStep,
      }),
      [
        current,
        currentId,
        focus,
        onComplete,
        items,
        allCompleted,
        setAllCompleted,
        allowNavigateToPrevStep,
      ],
    );

    return (
      <StepperContext.Provider value={value}>
        <motion.div
          transition={{ duration: 0 }}
          layout
          className={stepperClassNames}
          data-anv="stepper"
          style={styleCombined}
          {...rest}
          ref={useMergeRefs([tabRef, ref])}
        >
          {children}
        </motion.div>
      </StepperContext.Provider>
    );
  },
);

StepperElement.displayName = "Stepper";

/**
 * Stepper component for creating multi-step workflows and forms.
 *
 * Features:
 * - Multi-step navigation with visual progress indicators
 * - Keyboard navigation support with arrow keys
 * - Optional navigation to previous steps
 * - Smooth animations and transitions
 * - Accessible with proper ARIA roles and attributes
 * - Customizable step labels and content
 * - Completion state handling
 * - Layout utilities for positioning and spacing
 * - Motion animations for step transitions
 * - Context-based state management
 *
 * @example
 * <Stepper defaultIndex={0} allowNavigateToPrevStep>
 *   <Stepper.List>
 *     <Stepper.Step controls="step1">Step 1</Stepper.Step>
 *     <Stepper.Step controls="step2">Step 2</Stepper.Step>
 *     <Stepper.Step controls="step3">Step 3</Stepper.Step>
 *   </Stepper.List>
 *
 *   <Stepper.Panel id="step1">
 *     <h2>Step 1 Content</h2>
 *     <p>This is the first step content.</p>
 *   </Stepper.Panel>
 *
 *   <Stepper.Panel id="step2">
 *     <h2>Step 2 Content</h2>
 *     <p>This is the second step content.</p>
 *   </Stepper.Panel>
 *
 *   <Stepper.Panel id="step3">
 *     <h2>Step 3 Content</h2>
 *     <p>This is the final step content.</p>
 *   </Stepper.Panel>
 *
 *   <Stepper.FinalPanel>
 *     <h2>Completed!</h2>
 *     <p>All steps have been completed successfully.</p>
 *   </Stepper.FinalPanel>
 *
 *   <Stepper.PrevButton />
 *   <Stepper.NextButton />
 * </Stepper>
 */
export const Stepper = Object.assign(StepperElement, {
  /**
   * Individual step component for the Stepper.
   *
   * Features:
   * - Visual progress indicator with animated bar
   * - Check icon for completed steps
   * - Keyboard navigation support
   * - Accessible with proper ARIA attributes
   * - Click handling for step navigation
   * - Visual states for current, completed, and future steps
   * - Responsive design with mobile-friendly layout
   * - Focus management for keyboard users
   * - Custom styling support
   *
   * @example
   * <Stepper.Step controls="step1">
   *   Personal Information
   * </Stepper.Step>
   *
   * @example
   * <Stepper.Step
   *   controls="step2"
   *   onClick={(e, index) => console.log(`Clicked step ${index}`)}
   * >
   *   Contact Details
   * </Stepper.Step>
   */
  Step: StepperStep,

  /**
   * Container component for organizing and displaying stepper steps.
   *
   * Features:
   * - Groups multiple stepper steps together
   * - Displays current step label
   * - Responsive design with mobile-friendly layout
   * - Accessible with proper ARIA roles
   * - Automatic step organization
   * - Visual step indicators
   * - Context-based current step display
   * - Flexible layout with CSS Grid/Flexbox
   * - Container queries for responsive behavior
   *
   * @example
   * <Stepper.List>
   *   <Stepper.Step controls="step1">Step 1</Stepper.Step>
   *   <Stepper.Step controls="step2">Step 2</Stepper.Step>
   *   <Stepper.Step controls="step3">Step 3</Stepper.Step>
   * </Stepper.List>
   */
  List: StepperList,

  /**
   * Content panel component for displaying step content in the Stepper.
   *
   * Features:
   * - Displays content for the current step only
   * - Smooth fade-in/fade-out animations
   * - Automatic overflow handling with scroll support
   * - Accessible with proper ARIA roles
   * - Layout utilities for positioning and spacing
   * - Motion animations for smooth transitions
   * - Focus management for keyboard navigation
   * - Responsive design with flexible layout
   * - Context-based visibility control
   *
   * @example
   * <Stepper.Panel id="step1">
   *   <h2>Personal Information</h2>
   *   <form>
   *     <label htmlFor="name">Name:</label>
   *     <input id="name" type="text" />
   *   </form>
   * </Stepper.Panel>
   */
  Panel: StepperPanel,

  /**
   * Next button component for navigating to the next step in the Stepper.
   *
   * Features:
   * - Automatically advances to the next step
   * - Changes label to "Complete" on the final step
   * - Supports async validation with Promise return
   * - Prevents navigation when validation fails
   * - Hides when all steps are completed
   * - Accessible with proper button semantics
   * - Customizable appearance and styling
   * - Integration with stepper context
   * - Automatic completion handling
   *
   * @example
   * <Stepper.NextButton
   *   nextLabel="Continue"
   *   completeLabel="Finish"
   * />
   *
   * @example
   * <Stepper.NextButton
   *   onClick={async (e) => {
   *     const isValid = await validateForm();
   *     return isValid;
   *   }}
   *   appearance="primary"
   * />
   */
  NextButton: StepperNextButton,

  /**
   * Previous button component for navigating to the previous step in the Stepper.
   *
   * Features:
   * - Navigates to the previous step when available
   * - Hides when on the first step
   * - Restores navigation when returning from completion
   * - Accessible with proper button semantics
   * - Customizable label and styling
   * - Integration with stepper context
   * - Automatic focus management
   * - Support for completion state handling
   *
   * @example
   * <Stepper.PrevButton label="Go Back" />
   *
   * @example
   * <Stepper.PrevButton
   *   label="Previous Step"
   *   appearance="secondary"
   *   onClick={(e) => console.log('Going back')}
   * />
   */
  PrevButton: StepperPrevButton,

  /**
   * Final panel component displayed when all stepper steps are completed.
   *
   * Features:
   * - Displays only when all steps are completed
   * - Smooth fade-in animation on completion
   * - Automatic overflow handling with scroll support
   * - Accessible with proper ARIA roles
   * - Layout utilities for positioning and spacing
   * - Motion animations for smooth transitions
   * - Focus management for keyboard navigation
   * - Responsive design with flexible layout
   * - Context-based visibility control
   *
   * @example
   * <Stepper.FinalPanel>
   *   <h2>Congratulations!</h2>
   *   <p>You have successfully completed all steps.</p>
   *   <Button onClick={handleSubmit}>Submit</Button>
   * </Stepper.FinalPanel>
   */
  FinalPanel: StepperFinalPanel,
});
